import {
  CA_TERMED_BILL_TYPE,
  EXCHANGE_INDICATOR_TYPE,
  FUTURE_TERMED_BILL_TYPES,
} from '../constants/billingHistoryConstants';
import { MEMBER_DERIVED_LOB } from '../constants/memberConstants';
import {
  IBillDetails,
  IBillingHistorySpreadsheetDetails,
  IBillingHistoryViewListDetails,
  IBillsInfo,
  IBinderBill,
  IGovernmentProductDetails,
  IPaymentDetailsResponse,
  IPremiumDetails,
} from '../store/interface';
import {
  getLastDayOfMonth,
  isAfterCurrentDate,
  sortArrayDateValueByKey,
} from './dateHelper';

export const getSelectedCimDetails = (
  selectedCim: string | undefined,
  billsDetails: IPaymentDetailsResponse[] | undefined,
  selectedBillMppId?: number | null,
): IPaymentDetailsResponse => {
  if (selectedBillMppId) {
    return (
      (billsDetails &&
        billsDetails.find(
          ({ cim, mppId }) =>
            selectedCim === cim && mppId === selectedBillMppId,
        )) ||
      {}
    );
  } else {
    return (
      (billsDetails && billsDetails.find(({ cim }) => selectedCim === cim)) ||
      {}
    );
  }
};

export const getSortedBillsAscByDueDate = (
  bills: IPaymentDetailsResponse[],
) => {
  return sortArrayDateValueByKey({
    array: bills,
    key: 'dueDate',
  });
};

//TODO remove once we get sorted data from BE
export const getSortedBillsDscByDate = (
  bills: IBillingHistoryViewListDetails[] | IBillingHistorySpreadsheetDetails[],
) => {
  return sortArrayDateValueByKey({
    array: bills,
    key: 'billPeriod',
    ascSortType: false,
  });
};

export const checkForExpiringPolicy = (
  billName: string,
  premiumDetailsList: IPremiumDetails[],
) => {
  if (
    billName === FUTURE_TERMED_BILL_TYPES.EXPIRING_POLICY &&
    !premiumDetailsList.length
  )
    return true;
  return false;
};

export const getExchangeIndicatorFromBills = (binderBills: IBinderBill[]) => {
  binderBills.map((bills: IBinderBill) => {
    if (bills.exchangeIndicator?.toUpperCase() === 'ON EXCHANGE') {
      return bills.exchangeIndicator;
    }
  });
  return binderBills[0].exchangeIndicator;
};

// Separate for active / guest members to be kept separate from binder
export const getExchangeIndicatorFromMemberBills = (
  bills: IPaymentDetailsResponse[],
) => {
  const onExchangeBill = bills.find((bill) => {
    return bill.exchangeIndicator === EXCHANGE_INDICATOR_TYPE.ON_EXCHANGE;
  });

  if (onExchangeBill) {
    const { exchangeIndicator, cim, caseNumber } = onExchangeBill;
    return {
      exchangeIndicator,
      cim,
      caseNumber,
    };
  } else {
    const { exchangeIndicator, cim, caseNumber } = bills[0];
    return {
      exchangeIndicator,
      cim,
      caseNumber,
    };
  }
};

export const billPeriodFormat = 'YYYY-MM-DD';

export const futureTermedBillNameMapper = (
  billType: string | undefined,
  expiredText: string,
  expiringText: string,
  caTermedBillName: string,
) => {
  switch (billType) {
    case FUTURE_TERMED_BILL_TYPES.EXPIRED_POLICY:
      return expiredText;
    case FUTURE_TERMED_BILL_TYPES.EXPIRING_POLICY:
      return expiringText;
    case CA_TERMED_BILL_TYPE:
      return caTermedBillName;
    default:
      return billType;
  }
};

export const getNextPaymentAmount = (bills: IPaymentDetailsResponse[]) => {
  let billPremiumSum = 0;

  if (bills && bills.length > 0) {
    bills.map((bill: IPaymentDetailsResponse) => {
      bill?.premiumDetailsList?.map((premium: IPremiumDetails) => {
        billPremiumSum += premium.billedPremium;
      });
    });
  }

  return billPremiumSum;
};

export const isTwoMonthsPaymentRemaining = (bills: IPaymentDetailsResponse[]): boolean => {
  let totalMinimumAmountDue = 0;

  if (bills && bills.length > 0) {
    bills.forEach(bill => {
      if (bill.minimumAmountDue) {
        totalMinimumAmountDue += bill.minimumAmountDue;
      }
    });
  }

  return totalMinimumAmountDue > 0;
};

export const isOneMonthPaymentRemaining = (bills: IPaymentDetailsResponse[]): boolean => {
  let totalMinimumAmountDue = 0;
  let totalAmountDue = 0;

  if (bills && bills.length > 0) {
    bills.forEach(bill => {
      if (bill.minimumAmountDue) {
        totalMinimumAmountDue += bill.minimumAmountDue;
      }
      if (bill.amountDue) {
        totalAmountDue += bill.amountDue;
      }
    });
  }

  return totalMinimumAmountDue <= 0 && totalAmountDue > 0;
};

export const getTotalAmountDue = (bills: IPaymentDetailsResponse[]): number => {
  let totalAmountDue = 0;

  if (bills && bills.length > 0) {
    bills.forEach(bill => {
      if (bill.amountDue) {
        totalAmountDue += bill.amountDue;
      }
    });
  }

  return totalAmountDue;
};


export const getLatePaymentAmount = (bills: IPaymentDetailsResponse[]) => {
  let latePaymentAmount = 0;

  if (bills && bills.length > 0) {
    bills.map((bill: IPaymentDetailsResponse) => {
      if (bill.minimumAmountDue) {
        latePaymentAmount += bill.minimumAmountDue;
      }
    });
  }

  return latePaymentAmount;
};

export const checkRecurringPayment = (bills: IPaymentDetailsResponse[]) => {
  if (bills && bills.length > 0) {
    if (
      bills.find(
        (bill: IPaymentDetailsResponse) =>
          bill.billDraftMethod?.toUpperCase() === 'RECURRING_EFT',
      )
    ) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

// getBillsResponseInfo Is use to check if billPeriod is null or not and set the bill details and redirection
export const getBillsResponseInfo = (bills: IPaymentDetailsResponse[]) => {
  const billsInfo: IBillsInfo = {
    noBillIndicator: false,
    redirectToAllPayments: false,
    billInfoCim: '',
    billInfoCaseNumber: '',
    billInfoExchangeIndicator: '',
  };
  // billCounter use to keep count on bills with billPeriod value
  let billCounter = 0;
  // noBillCounter use to keep count on bills with billPeriod as null
  let noBillCounter = 0;

  bills &&
    bills.map((bill: IPaymentDetailsResponse) => {
      if (bill.billPeriod !== null) {
        billCounter++;
      } else {
        noBillCounter++;
      }
    });
  // set redirectToAllPayments true, only if bills with billPeriod value is present for more than one bill
  billsInfo.redirectToAllPayments = billCounter > 1;

  // set cim case and exchange indicator when bills lenght is greater than 1
  // along with one bill with billPeriod value and others with null value
  if (bills && bills.length > 1 && billCounter === 1 && noBillCounter > 0) {
    const bill = bills.find(
      (bill: IPaymentDetailsResponse) => bill.billPeriod !== null,
    );
    billsInfo.billInfoCim = bill?.cim || '';
    billsInfo.billInfoCaseNumber = bill?.caseNumber || '';
    billsInfo.billInfoExchangeIndicator = bill?.exchangeIndicator || '';
  }

  // set cim case and exchange indicator when bills length is greater than 1 and all with null billPeriod
  if (billCounter === 0 && noBillCounter > 0) {
    billsInfo.noBillIndicator = true;
    billsInfo.billInfoCim = bills[0].cim || '';
    billsInfo.billInfoCaseNumber = bills[0].caseNumber || '';
    billsInfo.billInfoExchangeIndicator = bills[0].exchangeIndicator || '';
  }
  return billsInfo;
};

export const updateAmountAndMinAmountForNoBills = (
  bills: IBillDetails,
  govtMember?: boolean,
) => {
  const updatedBillsWithAmount =
    bills.billDetails &&
    bills.billDetails.map((bill: IPaymentDetailsResponse) => ({
      ...bill,
      amountDue: 0,
      minimumAmountDue: 0,
    }));
  const billDetail = bills.billDetails[0];
  // adding subcriberId as cim for gov for no bill for single bill scenario for gov pdp and madp user
  const updatedBillsWithAmountForGovtMember = [
    {
      ...billDetail,
      amountDue: 0,
      minimumAmountDue: 0,
      cim: billDetail?.subcriberId || '',
    },
  ];
  bills['billDetails'] = govtMember
    ? updatedBillsWithAmountForGovtMember
    : updatedBillsWithAmount;
  return bills;
};

export const getGovtBillResponseInfo = (
  bills: IPaymentDetailsResponse[],
  derivedLob: MEMBER_DERIVED_LOB | undefined,
  noBillFound: boolean,
) => {
  const billsInfo: IBillsInfo = {
    noBillIndicator: false,
    redirectToAllPayments: false,
    billInfoCim: '',
    billInfoCaseNumber: '',
    billInfoExchangeIndicator: '',
  };
  if (
    (derivedLob === MEMBER_DERIVED_LOB.GOVERNMENT_PDP ||
      derivedLob === MEMBER_DERIVED_LOB.GOVERNMENT_MEDICARE) &&
    (bills?.length === 0 || noBillFound)
  ) {
    billsInfo.noBillIndicator = true;
  }

  if (bills?.length > 0) {
    billsInfo.billInfoCim = bills[0].cim || '';
  }
  if (bills?.length > 1) {
    billsInfo.redirectToAllPayments = true;
  }
  return billsInfo;
};

export const getDisplayAutopayDraftDate = (
  bills: IPaymentDetailsResponse[],
) => {
  // extra check to eliminate bill having autoPayDraftDate with unexpected value with empty string,null or key not present
  const filteredBillsWithAutopayDraftDate = bills.filter(
    (bill) => bill?.autoPayDraftDate,
  );
  const sortedBillsByAutopayDraftDate = sortArrayDateValueByKey({
    array: filteredBillsWithAutopayDraftDate,
    key: 'autoPayDraftDate',
  });
  return sortedBillsByAutopayDraftDate[0]?.autoPayDraftDate || '';
};

export const getNearestBillPeriod = (bills: IPaymentDetailsResponse[]) => {
  const filteredBillsWithBillPeriodDate = bills.filter(
    (bill) => bill?.billPeriod,
  );

  const sortedBillsByBillPeriodDate = sortArrayDateValueByKey({
    array: filteredBillsWithBillPeriodDate,
    key: 'billPeriod',
  });

  return sortedBillsByBillPeriodDate[0]?.billPeriod || '';
};

export const getProductDetail = (
  allProductDetails: IGovernmentProductDetails[],
  bill: IPaymentDetailsResponse,
  isSingleILB?: boolean,
) => {
  const productdetail =
    allProductDetails &&
    allProductDetails.find((productDetail: IGovernmentProductDetails) => {
      if (
        !isSingleILB &&
        bill.mppId &&
        productDetail.subcriberId === bill?.subcriberId &&
        productDetail.mppId === bill?.mppId
      ) {
        // added mppid check to identify bill when subscriber id's are same for multiple policies as mppid is different for all bills
        // This for Active Govt flow.
        return productDetail;
      } else if (
        isSingleILB &&
        productDetail.subcriberId === bill?.subcriberId
      ) {
        // This is for Guest Govt flow.
        return productDetail;
      }
    });
  return productdetail;
};

export const getActiveUserNextPaymentDate = (
  bills: IPaymentDetailsResponse[],
  serverDate: string,
  isBillPeriodFormat?: boolean,
) => {
  const nearestBillPeriod = getNearestBillPeriod(bills || []);
  const isBillPeriodAfterCurrentDate = isAfterCurrentDate(
    nearestBillPeriod,
    billPeriodFormat,
    serverDate,
  );

  return getLastDayOfMonth(
    isBillPeriodAfterCurrentDate ? nearestBillPeriod : serverDate,
    isBillPeriodFormat ? billPeriodFormat : undefined,
  );
};
